import { REDUCED_MOTION_MEDIA_QUERY } from "../../../../config/constants";
import LoginBackgroundRenderer from "./LoginBackgroundRenderer";

// The visual is hidden below this width, as it would be too narrow for the highlights; see styles.module.scss.
export const WIDE_MEDIA_QUERY = "(width >= 880px)";

// Caps the time a single frame advances, so the motion resumes where it paused after a hidden tab.
const MAX_FRAME_TIME = 100;

/**
 * Animates the login background on a canvas: starts the renderer once the visual is shown, and keeps it still for
 * users who prefer reduced motion.
 */
export default class LoginBackground {
	/**
	 * @param {HTMLCanvasElement} canvas The canvas to draw on.
	 */
	constructor( canvas ) {
		this.canvas = canvas;
		this.renderer = null;
		this.unsupported = false;
		this.frame = null;
		this.previousTimestamp = null;
		this.time = 0;
		this.wideQuery = window.matchMedia( WIDE_MEDIA_QUERY );
		this.reducedMotionQuery = window.matchMedia( REDUCED_MOTION_MEDIA_QUERY );
		this.resizeObserver = null;
		this.update = this.update.bind( this );
		this.tick = this.tick.bind( this );
	}

	/**
	 * Starts drawing, and follows changes in screen width and motion preference.
	 */
	start() {
		this.wideQuery.addEventListener( "change", this.update );
		this.reducedMotionQuery.addEventListener( "change", this.update );
		this.update();
	}

	/**
	 * Creates the renderer when the visual is shown, then animates or draws a still frame.
	 */
	update() {
		this.stopLoop();

		if ( ! this.wideQuery.matches || ! this.ensureRenderer() ) {
			return;
		}

		if ( this.reducedMotionQuery.matches ) {
			this.time = 0;
			this.renderer.render( 0 );
			return;
		}

		this.frame = window.requestAnimationFrame( this.tick );
	}

	/**
	 * @returns {boolean} Whether there is a renderer to draw with.
	 */
	ensureRenderer() {
		if ( this.renderer || this.unsupported ) {
			return Boolean( this.renderer );
		}

		this.renderer = LoginBackgroundRenderer.create( this.canvas );
		this.unsupported = ! this.renderer;

		if ( this.renderer && typeof ResizeObserver !== "undefined" ) {
			this.resizeObserver = new ResizeObserver( () => this.renderer.resize() );
			this.resizeObserver.observe( this.canvas );
		}

		return Boolean( this.renderer );
	}

	/**
	 * Draws a frame and requests the next.
	 *
	 * @param {number} timestamp The frame's timestamp in ms.
	 */
	tick( timestamp ) {
		if ( this.previousTimestamp !== null ) {
			this.time += Math.min( timestamp - this.previousTimestamp, MAX_FRAME_TIME );
		}

		this.previousTimestamp = timestamp;
		this.renderer.render( this.time );
		this.frame = window.requestAnimationFrame( this.tick );
	}

	/**
	 * Cancels the pending frame.
	 */
	stopLoop() {
		if ( this.frame !== null ) {
			window.cancelAnimationFrame( this.frame );
			this.frame = null;
		}

		this.previousTimestamp = null;
	}

	/**
	 * Stops drawing and listening.
	 */
	destroy() {
		this.stopLoop();
		this.wideQuery.removeEventListener( "change", this.update );
		this.reducedMotionQuery.removeEventListener( "change", this.update );

		if ( this.resizeObserver ) {
			this.resizeObserver.disconnect();
		}

		if ( this.renderer ) {
			this.renderer.destroy();
		}
	}
}
